/**
 * Discount Manager - Auto Add Gift Product (v2)
 * Manages automatic gift product addition based on discount conditions.
 *
 * v2 Logic:
 * - Uses line item properties ('_auto_gift': 'true') to "tag" the gift.
 * - This prevents conflicts with manually added versions of the same product.
 * - All qualification checks filter out this auto-gift to prevent circular calculations.
 */
class DiscountManager {
  constructor() {
    this.container = document.querySelector(".cart-drawer-discounts");
    if (!this.container) return;

    this.config = {
      discountType: this.container.dataset.discountType,
      giftProductId: parseInt(this.container.dataset.giftProductId) || null,
      giftProductHandle: this.container.dataset.giftProductHandle,
      minimumPrice: parseInt(this.container.dataset.minimumPrice) || 0,
      conditionProduct: this.container.dataset.conditionProduct,
      conditionCollection: this.container.dataset.conditionCollection,
      collectionProductIds: this.container.dataset.collectionProductIds
        ? this.container.dataset.collectionProductIds
            .split(",")
            .filter((id) => id.trim() !== "")
            .map((id) => parseInt(id.trim()))
        : [],
    };

    // Ensure we have the minimum required config
    if (!this.config.giftProductId) {
      console.error("Discount Manager: giftProductId is not set.");
      return;
    }

    this.notQualifiedSection = this.container.querySelector(
      "[data-not-qualified-section]"
    );
    this.qualifiedSection = this.container.querySelector(
      "[data-qualified-section]"
    );
    this.isProcessing = false;
    this.giftInCart = false; // Tracks the state of the *auto-added* gift

    this.init();
  }

  init() {
    // Listen to various cart update events

    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name.includes("/cart/")) {
          console.log(" /cart/ network:", entry.name);
          this.handleCartUpdate();
        }
      }
    }).observe({ entryTypes: ["resource"] });

    // Observe DOM changes in cart to detect updates
    this.observeCartChanges();

    // Initial check
    this.checkCartAndUpdate();
  }

  observeCartChanges() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        clearTimeout(this.checkTimeout);
        this.checkTimeout = setTimeout(() => this.handleCartUpdate(), 300);
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }

    const mainCart = document.querySelector("cart-items");
    if (mainCart) {
      const observer = new MutationObserver(() => {
        clearTimeout(this.checkTimeout);
        this.checkTimeout = setTimeout(() => this.handleCartUpdate(), 300);
      });
      observer.observe(mainCart, { childList: true, subtree: true });
    }
  }

  async handleCartUpdate() {
    if (this.isProcessing) return;
    await this.checkCartAndUpdate();
  }

  async checkCartAndUpdate() {
    try {
      const cart = await this.fetchCart();
      const qualifies = this.checkQualification(cart);
      const hasGift = this.hasGiftProduct(cart);

      this.giftInCart = hasGift;

      if (qualifies && !hasGift) {
        // Check if the gift product is already in cart (manually added)
        const giftAlreadyInCart = cart.items.some(
          (item) =>
            item.variant_id === this.config.giftProductId &&
            !this._isAutoGift(item)
        );

        if (giftAlreadyInCart) {
          // Gift is already in cart but not as auto-gift, just show qualified status
          this.updateStatus("qualified");
        } else {
          // Add gift product
          await this.addGiftProduct();
          this.updateStatus("qualified");
        }
      } else if (!qualifies && hasGift) {
        this.updateStatus("not-qualified");
        await this.removeGiftProduct(cart);
      } else if (qualifies && hasGift) {
        this.updateStatus("qualified");
      } else {
        this.updateStatus("not-qualified");
      }
    } catch (error) {
      console.error("Discount Manager Error:", error);
    }
  }

  async fetchCart() {
    const response = await fetch("/cart.js");
    return await response.json();
  }

  /**
   * Checks if an item is the auto-added gift.
   * @param {object} item - The cart line item.
   * @returns {boolean}
   */
  _isAutoGift(item) {
    return (
      item.variant_id === this.config.giftProductId &&
      item.properties &&
      item.properties["_auto_gift"] === "true"
    );
  }

  checkQualification(cart) {
    switch (this.config.discountType) {
      case "amount":
        return this.checkAmountCondition(cart);
      case "product":
        return this.checkProductCondition(cart);
      case "amount_and_collection":
        return this.checkCollectionAmountCondition(cart);
      default:
        return false;
    }
  }

  checkAmountCondition(cart) {
    // Check if cart total (excluding auto-gift) meets minimum
    const cartTotalWithoutGift = cart.items
      .filter((item) => !this._isAutoGift(item)) // Filter out the auto-gift
      .reduce((sum, item) => sum + item.final_line_price / 100, 0);

    return cartTotalWithoutGift >= this.config.minimumPrice;
  }

  checkProductCondition(cart) {
    // Check if condition product is in cart (and is not the auto-gift itself)
    return cart.items.some(
      (item) =>
        item.handle === this.config.conditionProduct && !this._isAutoGift(item)
    );
  }

  checkCollectionAmountCondition(cart) {
    // Check if collection products total (excluding auto-gift) meets minimum
    const collectionTotal = cart.items
      .filter(
        (item) =>
          this.config.collectionProductIds.includes(item.product_id) &&
          !this._isAutoGift(item) // Filter out auto-gift from collection total
      )
      .reduce((sum, item) => sum + item.final_line_price / 100, 0);

    return collectionTotal >= this.config.minimumPrice;
  }

  /**
   * Checks if the *auto-added* gift is in the cart.
   * @param {object} cart - The cart object.
   * @returns {boolean}
   */
  hasGiftProduct(cart) {
    return cart.items.some((item) => this._isAutoGift(item));
  }

  /**
   * Adds the gift product with the auto-gift property.
   */
  async addGiftProduct() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      // Ensure giftProductId is a valid number
      if (!this.config.giftProductId || isNaN(this.config.giftProductId)) {
        console.error(
          "Discount Manager: Invalid giftProductId",
          this.config.giftProductId
        );
        this.isProcessing = false;
        return;
      }

      const formData = {
        items: [
          {
            id: this.config.giftProductId,
            quantity: 1,
            properties: {
              _auto_gift: "true",
            },
          },
        ],
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        // Log the actual error from Shopify
        const errorMessage =
          responseData.description ||
          responseData.message ||
          "Failed to add gift product";
        console.error("Error adding gift product:", errorMessage, responseData);
        throw new Error(errorMessage);
      }

      await this.reloadCartSections();
      this.giftInCart = true;
      this.updateStatus("qualified");
    } catch (error) {
      console.error("Error adding gift product:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Removes ONLY the auto-added gift product.
   * Note: This function is not used in the current `checkCartAndUpdate` logic
   * based on user requirements, but is here for completeness.
   */
  async removeGiftProduct(cart) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      // Find the specific line item that is our auto-gift
      const giftItem = cart.items.find((item) => this._isAutoGift(item));

      if (!giftItem) {
        this.isProcessing = false;
        return; // No auto-gift found to remove
      }

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: giftItem.key, // Use 'key' for items with properties
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to remove gift product");
      }

      await this.reloadCartSections();
      this.giftInCart = false;
      this.updateStatus("not-qualified");
    } catch (error) {
      console.error("Error removing gift product:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  async reloadCartSections() {
    // Reload cart drawer
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      try {
        const response = await fetch(
          window.location.pathname + "?section_id=cart-drawer"
        );
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newCartBody = doc.querySelector("#CartDrawer-Body");
        const currentCartBody = document.querySelector("#CartDrawer-Body");

        if (newCartBody && currentCartBody) {
          currentCartBody.innerHTML = newCartBody.innerHTML;
        }
      } catch (error) {
        console.error("Error reloading cart drawer:", error);
      }
    }

    const mainCart = document.querySelector("cart-items");
    if (mainCart) {
      try {
        const mainCartId =
          document.getElementById("main-cart-items")?.dataset.id;
        if (mainCartId) {
          const response = await fetch(
            window.location.pathname + `?section_id=${mainCartId}`
          );
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newCartItems = doc.querySelector("cart-items");

          if (newCartItems && mainCart) {
            mainCart.innerHTML = newCartItems.innerHTML;
          }
        }
      } catch (error) {
        console.error("Error reloading main cart:", error);
      }
    }

    // Update cart counter
    try {
      const response = await fetch("/?section_id=cart-counter");
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newCounter = doc.querySelector("#shopify-section-cart-counter");
      const currentCounter = document.querySelector(
        "#shopify-section-cart-counter"
      );

      if (newCounter && currentCounter) {
        currentCounter.innerHTML = newCounter.innerHTML;
      }
    } catch (error) {
      console.error("Error updating cart counter:", error);
    }

    // Re-initialize elements after DOM update
    setTimeout(() => {
      this.container = document.querySelector(".cart-drawer-discounts");
      if (this.container) {
        this.notQualifiedSection = this.container.querySelector(
          "[data-not-qualified-section]"
        );
        this.qualifiedSection = this.container.querySelector(
          "[data-qualified-section]"
        );
      }
    }, 100);
  }

  updateStatus(status) {
    if (!this.container) return;

    if (status === "qualified") {
      if (this.notQualifiedSection)
        this.notQualifiedSection.style.display = "none";
      if (this.qualifiedSection) this.qualifiedSection.style.display = "flex";
    } else {
      if (this.notQualifiedSection)
        this.notQualifiedSection.style.display = "flex";
      if (this.qualifiedSection) this.qualifiedSection.style.display = "none";
    }
  }

  getTranslation(key) {
    // This is a placeholder. Use Shopify's translation system in production.
    const translations = {
      en: {
        "cart.discount.gift_added_to_cart": "Gift Added to your Cart!",
        "cart.discount.gift_kept_in_cart":
          "You have the gift item in your cart!",
        "cart.discount.conditions_not_met": "Add more to qualify for this gift",
      },
      he: {
        "cart.discount.gift_added_to_cart": "המתנה נוספה לסל!",
        "cart.discount.gift_kept_in_cart": "יש לך את מוצר המתנה בסל!",
        "cart.discount.conditions_not_met": "הוסף עוד כדי לקבל את המתנה",
      },
    };
    const locale = document.documentElement.lang || "en";
    return translations[locale]?.[key] || translations["en"][key];
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new DiscountManager();
  });
} else {
  new DiscountManager();
}
